import type { SupabaseClient } from "@supabase/supabase-js";
import type { Dayjs } from "@punchup/utils/dayjs";
import { logException } from "@punchup/sentry";
import type { Database, Json } from "../../supabase.types";
import { BookingResponseStatus } from "../../models/bookings";
import type { ShowReminderCandidate } from "../../models/show-reminders";
import type { VenuePageTimezones } from "../../models/venue_pages";

const PAGE_SIZE = 500;
const WIDEST_VENUE_UTC_OFFSET_HOURS = 15;
const LONGEST_HONORED_LEAD_DAYS = 30;

const NAIVE_TIMESTAMP = "YYYY-MM-DDTHH:mm:ss";

const CANDIDATE_COLUMNS = `
  id,
  reminder_before_show_value,
  reminder_before_show_unit,
  comedian_shows!inner(
    id,
    title,
    datetime,
    venue_id,
    show_comedian_pairings(comedian_id)
  ),
  booking_contact_responses!inner(
    id,
    status,
    show_id,
    comedian_id,
    contact_name,
    contact_email,
    performance_time,
    performance_duration,
    lineup_position,
    comedians(display_name),
    booking_contact_invitations!inner(
      booking_contacts!inner(name, email, phone),
      bookings!inner(venue_pages!inner(id, name, slug, timezones))
    )
  )
`;

function asTimezones(value: Json | null): VenuePageTimezones {
  return Array.isArray(value) ? (value as unknown as VenuePageTimezones) : [];
}

/** Every switched-on, unsent reminder for a confirmed booking, through a contact the venue has not deleted, on a show the venue has not deleted and that falls in the fetch window; `decideShowReminder` decides which are due. */
export async function fetchShowReminderCandidates(
  supabase: SupabaseClient<Database>,
  { now }: { now: Dayjs }
): Promise<ShowReminderCandidate[]> {
  const utcNow = now.utc();
  const windowStart = utcNow
    .subtract(WIDEST_VENUE_UTC_OFFSET_HOURS, "hour")
    .format(NAIVE_TIMESTAMP);
  const windowEnd = utcNow
    .add(LONGEST_HONORED_LEAD_DAYS, "day")
    .add(WIDEST_VENUE_UTC_OFFSET_HOURS, "hour")
    .format(NAIVE_TIMESTAMP);

  const candidates: ShowReminderCandidate[] = [];
  let lastSettingId: string | null = null;

  for (;;) {
    const inWindow = supabase
      .from("performance_notification_settings")
      .select(CANDIDATE_COLUMNS)
      .eq("send_comedian_reminder", true)
      .is("reminder_sent_at", null)
      .is("comedian_shows.deleted_at", null)
      .eq("booking_contact_responses.status", BookingResponseStatus.CONFIRMED)
      .eq(
        "booking_contact_responses.booking_contact_invitations.booking_contacts.is_active",
        true
      )
      .gte("comedian_shows.datetime", windowStart)
      .lte("comedian_shows.datetime", windowEnd);
    const { data, error } = await (
      lastSettingId === null ? inWindow : inWindow.gt("id", lastSettingId)
    )
      .order("id")
      .limit(PAGE_SIZE);

    if (error) {
      logException(error, {
        data: "[show-reminders] Failed to fetch reminder candidates",
      });
      throw error;
    }

    for (const row of data ?? []) {
      const show = row.comedian_shows;
      const response = row.booking_contact_responses;
      const invitation = response.booking_contact_invitations;
      const contact = invitation.booking_contacts;
      const venuePage = invitation.bookings.venue_pages;

      candidates.push({
        settingId: row.id,
        leadValue: row.reminder_before_show_value,
        leadUnit: row.reminder_before_show_unit,
        response: {
          id: response.id,
          status: response.status,
          show_id: response.show_id,
          comedian_id: response.comedian_id,
          performance_time: response.performance_time,
          performance_duration: response.performance_duration,
          lineup_position: response.lineup_position,
        },
        comedian: response.comedians,
        show: {
          id: show.id,
          title: show.title,
          datetime: show.datetime,
          venue_id: show.venue_id,
          lineupComedianIds: show.show_comedian_pairings.map(
            (pairing) => pairing.comedian_id
          ),
        },
        venuePage: {
          id: venuePage.id,
          name: venuePage.name,
          slug: venuePage.slug,
          timezones: asTimezones(venuePage.timezones),
        },
        contact: {
          name: contact.name ?? response.contact_name,
          email: contact.email ?? response.contact_email,
          phone: contact.phone,
        },
      });
    }

    if (!data || data.length < PAGE_SIZE) return candidates;
    lastSettingId = data[data.length - 1].id;
  }
}
